import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useRef, useState } from 'react';
import { compact } from 'lodash-es';
import { BottomSheet, BottomSheetContentLayout } from '../BottomSheet';
import { Typography } from '../Typography';
import { BaseTextField } from './BaseTextField/BaseTextField';
import { MobileTextFieldTID } from './BaseTextField/BaseTextField.utils';
import { MobileSummaryCard } from './MobileSummaryCard';
import styles from './MobileTextField.module.scss';
const TextFieldInBottomSheet = /*#__PURE__*/ forwardRef(function TextFieldInBottomSheet(props, ref) {
    var _ref;
    var _props_mobile, _props_mobile1;
    const { validateInBottomSheet, width: _width, minWidth: _minWidth, InputProps: outerInputProps, onDone } = props, rest = _object_without_properties(props, [
        "validateInBottomSheet",
        "width",
        "minWidth",
        "InputProps",
        "onDone"
    ]);
    const [tempValue, setTempValue] = useState(props.value);
    const sheetInputRef = useRef(null);
    const renderSheetEndAdornment = (_props_mobile = props.mobile) === null || _props_mobile === void 0 ? void 0 : _props_mobile.renderSheetEndAdornment;
    // WHY: the outer field's adornment is bound to the outer input and setter, so reusing it here
    // would edit the field behind the sheet and be overwritten on save.
    const sheetInputProps = renderSheetEndAdornment ? _object_spread_props(_object_spread({}, outerInputProps), {
        endAdornment: renderSheetEndAdornment({
            inputRef: sheetInputRef,
            setValue: setTempValue
        })
    }) : outerInputProps;
    // WHY: consumers bind their own inputRef to the sheet's input — compose, never replace.
    const bindSheetInput = (element)=>{
        sheetInputRef.current = element;
        const consumerInputRef = rest.inputRef;
        if (typeof consumerInputRef === 'function') consumerInputRef(element);
        else if (consumerInputRef && typeof consumerInputRef === 'object') consumerInputRef.current = element;
    };
    const validationResult = validateInBottomSheet === null || validateInBottomSheet === void 0 ? void 0 : validateInBottomSheet(tempValue);
    const error = validationResult ? validationResult.error : props.error;
    const helperText = validationResult ? validationResult.helperText : props.helperText;
    function handleSave() {
        if (tempValue !== props.value && props.onChange) {
            props.onChange({
                target: {
                    value: tempValue
                }
            });
        }
        onDone();
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentClassName: styles.bottomSheetContent,
        footer: {
            primaryAction: {
                text: (_ref = (_props_mobile1 = props.mobile) === null || _props_mobile1 === void 0 ? void 0 : _props_mobile1.saveButtonLabel) !== null && _ref !== void 0 ? _ref : 'Save',
                onClick: ()=>handleSave(),
                bi: {
                    name: 'save'
                }
            }
        },
        children: /*#__PURE__*/ _jsx(BaseTextField, _object_spread_props(_object_spread({}, rest), {
            collapseHelperText: true,
            value: tempValue,
            error: error,
            helperText: helperText,
            onChange: (e)=>setTempValue(e.target.value),
            onKeyDown: ()=>{},
            InputProps: sheetInputProps,
            inputRef: bindSheetInput,
            ref: ref
        }))
    });
});
// WHY: see note on BaseTextField — forwardRef + MUI types collapses BiProps' discriminated union.
const MobileTextFieldImpl = /*#__PURE__*/ forwardRef(function MobileTextField(props, ref) {
    var _ref, _ref1;
    const { testId, error, label, value, helperText, disabled, mobile } = props;
    // eslint-disable-next-line no-restricted-syntax -- WHY: BottomSheet IS the declarative overlay the rule points at, and it takes `open` as a prop, so this seagull surface owns that state itself instead of deferring to showModal.
    const [isOpen, setIsOpen] = useState(false);
    const closeEditSheet = ()=>setIsOpen(false);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(MobileSummaryCard, {
                testId: testId,
                items: [
                    {
                        title: (_ref = mobile === null || mobile === void 0 ? void 0 : mobile.cardTitle) !== null && _ref !== void 0 ? _ref : label,
                        titleTestId: MobileTextFieldTID.Label(testId),
                        body: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    ellipsis: true,
                                    testId: MobileTextFieldTID.Value(testId),
                                    skin: error ? 'error' : 'standard',
                                    children: (_ref1 = mobile === null || mobile === void 0 ? void 0 : mobile.displayValue) !== null && _ref1 !== void 0 ? _ref1 : value
                                }),
                                helperText && /*#__PURE__*/ _jsx(Typography, {
                                    testId: MobileTextFieldTID.HelperText(testId),
                                    skin: error ? 'error' : 'secondary',
                                    children: helperText
                                })
                            ]
                        }),
                        actions: compact([
                            !disabled && {
                                type: 'edit',
                                testId: MobileTextFieldTID.EditButton(testId),
                                onClick: ()=>setIsOpen(true),
                                // WHY: inherent action opening the edit bottom-sheet; consumers cannot rename it (rule 6a) and it dispatches no onChange, so it cannot double-fire.
                                bi: {
                                    name: 'edit'
                                }
                            }
                        ])
                    }
                ]
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: isOpen,
                onClose: closeEditSheet,
                closeSheetOnLeave: true,
                children: /*#__PURE__*/ _jsx(TextFieldInBottomSheet, _object_spread_props(_object_spread({}, props), {
                    onDone: closeEditSheet,
                    ref: ref
                }))
            })
        ]
    });
});
export const MobileTextField = MobileTextFieldImpl;
